import { useCallback, useEffect, useRef, useState } from "react";

import axios from "axios";

import { auth } from "@/lib/firebase";
import { createAuthHeaders } from "@/lib/api-client";

import { ordinal, parseMonthlyCron, weekdayName } from "./broadcast-schedule";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

/**
 * How long broadcast captures (screenshots/PDFs) are stored before automatic cleanup.
 * Shown to users for transparency. Keep in sync with the engine's ARTIFACT_RETENTION_DAYS.
 */
export const BROADCAST_RETENTION_DAYS = 30;

// ─── Types (mirror the Kaze broadcasts producer API) ───

export type BroadcastSourceType = "optidev-project" | "url";
export type BroadcastChannelType =
  | "slack"
  | "webhook"
  | "pdf"
  | "teams"
  | "email";
export type RunStatus = "pending" | "capturing" | "captured" | "failed";
/** How a multi-project broadcast's N captures are delivered per channel. */
export type DeliveryMode = "separate" | "combined-images" | "combined-pdf";

export interface BroadcastSource {
  type: BroadcastSourceType;
  projectId?: string;
  url?: string;
}

/** Channel as returned by the engine (never includes raw credentials). */
export interface BroadcastChannel {
  id: string;
  type: string;
  config?: Record<string, unknown>;
}

export interface Broadcast {
  id: string;
  title: string;
  source: BroadcastSource;
  /** Present for multi-project (workspace-level) broadcasts. */
  sources?: BroadcastSource[] | null;
  deliveryMode?: DeliveryMode;
  cron: string;
  timezone: string;
  enabled: boolean;
  channels: BroadcastChannel[];
  createdAt?: string;
  updatedAt?: string;
  /**
   * Set only when the ENGINE paused the broadcast itself (after repeated failed scheduled
   * runs); null when the user paused it or it is running. Cleared on resume. Read-only.
   */
  autoPausedAt?: string | null;
  /** `auto:failed-runs` today. */
  pauseReason?: string | null;
  /** The run / delivery error that tipped it into the auto-pause. */
  lastError?: string | null;
}

/** Engine-written reason code for a broadcast the engine paused itself (os-reporting-engine#4). */
export const AUTO_PAUSE_REASON_FAILED_RUNS = "auto:failed-runs";

/**
 * True when the ENGINE paused this broadcast after repeated failed scheduled runs — as opposed
 * to the user flipping the switch. The reason is cleared server-side on resume.
 */
export function isAutoPaused(
  broadcast: Pick<Broadcast, "enabled" | "pauseReason">,
): boolean {
  return !broadcast.enabled && broadcast.pauseReason === AUTO_PAUSE_REASON_FAILED_RUNS;
}

/** One delivery attempt for a run (nested under the run — no separate endpoint). */
export interface RunDelivery {
  id: string;
  channelType: string;
  status: string;
  error?: string | null;
  deliveredAt?: string | null;
  /** Destination channel id/name (Slack) — round-tripped so deliveries deep-link. */
  channelId?: string | null;
  channelName?: string | null;
  /** For Slack, the posted file permalink (http URL) used by the "Open" link. */
  providerMessageId?: string | null;
}

/** True when the value is an http(s) URL we can safely open in a new tab. */
export function isHttpUrl(value: string | null | undefined): value is string {
  return typeof value === "string" && /^https?:\/\//.test(value);
}

/**
 * Paging metadata the engine returns on every `{ items, meta }` list response.
 * `hasMore` gates load-more directly — never compare loaded length against total.
 */
export interface PageMeta {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

/** Standard envelope for paged list endpoints. */
export interface PagedList<T> {
  items: T[];
  meta: PageMeta;
}

export interface BroadcastRun {
  id: string;
  status: RunStatus;
  /** ISO instant the run was due for; the API turns a manual run's slot into its trigger time. */
  scheduledFor: string;
  manual: boolean;
  artifactBytes?: number | null;
  captureMs?: number | null;
  error?: string | null;
  createdAt?: string;
  deliveries?: RunDelivery[];
}

/**
 * When a run happened, for display: the slot it was due for, so a run that started late — or
 * queued behind others at a busy hour — still reads as its scheduled time, matching the date on
 * everything the engine delivered (file names, email, webhook) and OptiSigns. `createdAt` (worker
 * pickup) only backs rows that carry no slot.
 */
export function runAt(run: Pick<BroadcastRun, "scheduledFor" | "createdAt">): string {
  return run.scheduledFor || run.createdAt || "";
}

/** One stored capture in the Captures library. */
export interface CaptureItem {
  runId: string;
  broadcastId: string;
  broadcastName: string;
  capturedAt: string;
  scheduledFor: string;
  manual: boolean;
  status: string;
  /** Distinct channel types this capture was delivered to. */
  channels: string[];
  kind: "pdf" | "image";
  bytes: number | null;
  /** Presigned download URL, or null once retention-swept. */
  artifactUrl: string | null;
  expired: boolean;
  keep: boolean;
}

/** Captures library filters (all optional). */
export interface CaptureFilter {
  from?: string;
  to?: string;
  broadcastId?: string;
  channel?: string;
  status?: string;
  search?: string;
}

/** Captures envelope — the paged list plus tenant-wide aggregates. */
export interface CapturesResult extends PagedList<CaptureItem> {
  fileCount: number;
  totalBytes: number;
}

/** A Slack connection the user can pick a bot token from. */
export interface SlackConnection {
  id: string;
  /** Connection display name (matches the backend `name` column). */
  name: string;
}

/** A Slack channel the connection's bot can post to. */
export interface SlackChannel {
  id: string;
  name: string;
}

/**
 * Result of listing a connection's channels. `ok:false` means Slack refused
 * (e.g. missing channels:read scope) — the UI falls back to manual ID entry.
 * `ok:true` with `error: 'partial_list'` means the list was cut short (page cap,
 * time budget or rate limit) — some channels may be missing.
 */
export interface SlackChannelListResult {
  ok: boolean;
  channels: SlackChannel[];
  error?: string;
}

// ─── Create / update payloads (match the Kaze CreateBroadcastDto) ───

export interface CreateBroadcastChannel {
  type: BroadcastChannelType;
  /** slack: destination channel id */
  channelId?: string;
  /** slack: destination channel display name (round-tripped for the UI) */
  channelName?: string;
  /** slack: which Kaze Slack connection to pull the bot token from */
  slackConnectionId?: string;
  /** webhook / teams: destination endpoint (webhook URL) */
  url?: string;
  /** slack / teams: optional caption */
  message?: string;
  /** email: recipient addresses */
  recipients?: string[];
  /** email: optional subject line */
  subject?: string;
}

/** Deep link that opens a Slack channel in the user's Slack client. */
export function slackChannelLink(channelId: string): string {
  return `https://slack.com/app_redirect?channel=${encodeURIComponent(channelId)}`;
}

/** Channels whose name contains the search, ignoring case and a leading "#" (same as OptiSigns). */
export function filterSlackChannels(channels: SlackChannel[], search: string): SlackChannel[] {
  const query = search.trim().replace(/^#/, "").toLowerCase();
  return channels.filter((ch) => ch.name.toLowerCase().includes(query));
}

/** Compact relative time, e.g. "2h ago", "3d ago", "just now". */
export function relativeTime(iso: string | undefined | null): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

export interface CreateBroadcastDto {
  title: string;
  /** Single source (per-project) OR sources[] (workspace-level) — provide exactly one. */
  source?: BroadcastSource;
  sources?: BroadcastSource[];
  deliveryMode?: DeliveryMode;
  cron: string;
  timezone: string;
  enabled?: boolean;
  channels: CreateBroadcastChannel[];
}

export type UpdateBroadcastDto = Partial<CreateBroadcastDto>;

// ─── API client (lib/api pattern — axios + createAuthHeaders) ───

async function authHeaders(): Promise<Record<string, string>> {
  const token = await auth.getIdToken();
  return createAuthHeaders(token!);
}

export const broadcastsApi = {
  /**
   * Paged broadcast list — `{ items, meta }`; defaults to the newest 20. A `projectId` narrows it
   * to the broadcasts capturing that project; the engine does the narrowing, so `meta` still
   * describes what comes back.
   */
  async getBroadcasts(
    page: BroadcastListQuery = {},
  ): Promise<PagedList<Broadcast>> {
    const response = await axios.get(`${API_URL}/api/broadcasts`, {
      headers: await authHeaders(),
      params: page,
    });
    return response.data;
  },

  /** One broadcast by id — used by the delivered-message deep link (`?broadcastId=`). */
  async getBroadcast(broadcastId: string): Promise<Broadcast> {
    const response = await axios.get(
      `${API_URL}/api/broadcasts/${encodeURIComponent(broadcastId)}`,
      { headers: await authHeaders() },
    );
    return response.data;
  },

  async createBroadcast(dto: CreateBroadcastDto): Promise<Broadcast> {
    const response = await axios.post(`${API_URL}/api/broadcasts`, dto, {
      headers: await authHeaders(),
    });
    return response.data;
  },

  async updateBroadcast(broadcastId: string, dto: UpdateBroadcastDto): Promise<Broadcast> {
    const response = await axios.patch(
      `${API_URL}/api/broadcasts/${broadcastId}`,
      dto,
      { headers: await authHeaders() },
    );
    return response.data;
  },

  async deleteBroadcast(broadcastId: string): Promise<void> {
    await axios.delete(`${API_URL}/api/broadcasts/${broadcastId}`, {
      headers: await authHeaders(),
    });
  },

  async runNow(broadcastId: string): Promise<{ accepted: true; jobId: string }> {
    const response = await axios.post(
      `${API_URL}/api/broadcasts/${broadcastId}/runs`,
      {},
      { headers: await authHeaders() },
    );
    return response.data;
  },

  /** Paged run history — `{ items, meta }`; defaults to the newest 20. */
  async getRuns(
    broadcastId: string,
    page: { limit?: number; offset?: number } = {},
  ): Promise<PagedList<BroadcastRun>> {
    const response = await axios.get(
      `${API_URL}/api/broadcasts/${broadcastId}/runs`,
      { headers: await authHeaders(), params: page },
    );
    return response.data;
  },

  /** Retry ONE failed delivery — the engine re-sends the stored capture. */
  async retryDelivery(deliveryId: string): Promise<{ queued: boolean }> {
    const response = await axios.post(
      `${API_URL}/api/broadcasts/deliveries/${deliveryId}/retry`,
      {},
      { headers: await authHeaders() },
    );
    return response.data;
  },

  /** Captures library — paged `{ items, meta, fileCount, totalBytes }`. */
  async getCaptures(
    filter: CaptureFilter & { limit?: number; offset?: number } = {},
  ): Promise<CapturesResult> {
    const response = await axios.get(`${API_URL}/api/broadcasts/captures`, {
      headers: await authHeaders(),
      params: filter,
    });
    return response.data;
  },

  /** Remove one capture from the library (hides the row, frees the R2 bytes). */
  async deleteCapture(runId: string): Promise<void> {
    await axios.delete(`${API_URL}/api/broadcasts/captures/${runId}`, {
      headers: await authHeaders(),
    });
  },

  async getSlackConnections(): Promise<SlackConnection[]> {
    const response = await axios.get(
      `${API_URL}/api/broadcasts/slack-connections`,
      { headers: await authHeaders() },
    );
    return response.data;
  },

  async getSlackChannels(connectionId: string): Promise<SlackChannelListResult> {
    const response = await axios.get(
      `${API_URL}/api/broadcasts/slack-connections/${connectionId}/channels`,
      { headers: await authHeaders() },
    );
    return response.data;
  },
};

/** Broadcast list request: a page, optionally scoped to one project. */
export interface BroadcastListQuery {
  limit?: number;
  offset?: number;
  projectId?: string;
}

// ─── Hooks ───

/**
 * Page sizes for the Broadcasts and Snapshots tables — the same list as OptiSigns. It stops at 50
 * because the engine refuses a page larger than that.
 */
export const BROADCAST_PAGE_SIZES = [10, 25, 50] as const;
export const DEFAULT_BROADCAST_PAGE_SIZE = 25;
/** The engine's per-workspace broadcast quota — one 50-row page is the whole account. */
export const MAX_BROADCASTS_PER_WORKSPACE = 50;

/** One page of a paged table. */
export interface PageRequest {
  limit: number;
  offset: number;
}

export interface UseBroadcastsResult {
  broadcasts: Broadcast[];
  loading: boolean;
  error: string | null;
  /** Server truth: another page exists. */
  hasMore: boolean;
  total: number;
  /** Refetch the page on screen (after any mutation). */
  refresh: () => Promise<void>;
}

/**
 * Fetches one page of scheduled broadcasts; call refresh() after any mutation. With a `projectId`
 * it lists the broadcasts that capture that project (the per-project tab); without one, the whole
 * workspace's. The filter is applied server-side, so `hasMore`/`total` stay true for the scope.
 * A response for a page the user has already left is dropped.
 */
export function useBroadcasts(
  scope: { projectId?: string } = {},
  page: PageRequest = { limit: DEFAULT_BROADCAST_PAGE_SIZE, offset: 0 },
): UseBroadcastsResult {
  const { projectId } = scope;
  const { limit, offset } = page;
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [meta, setMeta] = useState<PageMeta | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const requestKey = `${projectId ?? ""}:${limit}:${offset}`;
  const keyRef = useRef(requestKey);
  keyRef.current = requestKey;

  const refresh = useCallback(async (): Promise<void> => {
    const key = requestKey;
    try {
      const data = await broadcastsApi.getBroadcasts({ limit, offset, projectId });
      if (keyRef.current !== key) return; // the user paged on — drop this response
      setBroadcasts(data.items);
      setMeta(data.meta);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch broadcasts:", err);
      if (keyRef.current === key) setError("Failed to load broadcasts");
    } finally {
      if (keyRef.current === key) setLoading(false);
    }
  }, [requestKey, limit, offset, projectId]);

  useEffect(() => {
    setLoading(true);
    void refresh();
  }, [refresh]);

  return {
    broadcasts,
    loading,
    error,
    hasMore: meta?.hasMore ?? false,
    total: meta?.total ?? broadcasts.length,
    refresh,
  };
}

export interface UseCapturesResult {
  captures: CaptureItem[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  /** Snapshots matching the filter (drives the pagination footer). */
  total: number;
  /** Workspace-wide totals (unaffected by paging). */
  fileCount: number;
  totalBytes: number;
  refresh: () => Promise<void>;
}

/**
 * Fetches one page of the Snapshots library for the given filter. Filter and page form one request
 * key; a response for a key the user has already left is dropped. Paging gates on `meta.hasMore`.
 */
export function useCaptures(filter: CaptureFilter, page: PageRequest): UseCapturesResult {
  const [captures, setCaptures] = useState<CaptureItem[]>([]);
  const [meta, setMeta] = useState<PageMeta | null>(null);
  const [agg, setAgg] = useState<{ fileCount: number; totalBytes: number }>({
    fileCount: 0,
    totalBytes: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const requestKey = JSON.stringify([filter, page.limit, page.offset]);
  const keyRef = useRef(requestKey);
  keyRef.current = requestKey;

  const refresh = useCallback(async (): Promise<void> => {
    const key = requestKey;
    try {
      const data = await broadcastsApi.getCaptures({
        ...filter,
        limit: page.limit,
        offset: page.offset,
      });
      if (keyRef.current !== key) return;
      setCaptures(data.items);
      setMeta(data.meta);
      setAgg({ fileCount: data.fileCount, totalBytes: data.totalBytes });
      setError(null);
    } catch (err) {
      console.error("Failed to fetch snapshots:", err);
      if (keyRef.current === key) setError("Failed to load snapshots");
    } finally {
      if (keyRef.current === key) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- requestKey is the stable identity
  }, [requestKey]);

  useEffect(() => {
    setLoading(true);
    void refresh();
  }, [refresh]);

  return {
    captures,
    loading,
    error,
    hasMore: meta?.hasMore ?? false,
    total: meta?.total ?? captures.length,
    fileCount: agg.fileCount,
    totalBytes: agg.totalBytes,
    refresh,
  };
}

export interface UseSlackConnectionsResult {
  connections: SlackConnection[];
  loading: boolean;
}

/** Loads the workspace's Slack connections lazily — only while `enabled`. */
export function useSlackConnections(enabled: boolean): UseSlackConnectionsResult {
  const [connections, setConnections] = useState<SlackConnection[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    setLoading(true);
    broadcastsApi
      .getSlackConnections()
      .then((data) => {
        if (!cancelled) setConnections(data);
      })
      .catch((err) => {
        console.error("Failed to load Slack connections:", err);
        if (!cancelled) setConnections([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { connections, loading };
}

export interface UseSlackChannelsResult {
  channels: SlackChannel[];
  loading: boolean;
  /** true once a fetch resolved with ok:false (Slack refused → manual entry). */
  unavailable: boolean;
  /** true when the list came back cut short (`partial_list`) — some channels may be missing. */
  partial: boolean;
}

/**
 * Loads a Slack connection's channels for the picker. Resets when the connection
 * changes; on any failure (or Slack ok:false) sets `unavailable` so the caller
 * falls back to manual channel-ID entry.
 */
export function useSlackChannels(connectionId: string | null): UseSlackChannelsResult {
  const [channels, setChannels] = useState<SlackChannel[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [unavailable, setUnavailable] = useState<boolean>(false);
  const [partial, setPartial] = useState<boolean>(false);

  useEffect(() => {
    if (!connectionId) {
      setChannels([]);
      setUnavailable(false);
      setPartial(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setUnavailable(false);
    setPartial(false);
    broadcastsApi
      .getSlackChannels(connectionId)
      .then((res) => {
        if (cancelled) return;
        const isPartial = res.ok && res.error === "partial_list";
        setPartial(isPartial);
        if (res.ok && res.channels.length > 0) {
          setChannels(res.channels);
        } else {
          setChannels([]);
          // A cut-short list is not a Slack refusal — the partial hint covers it.
          setUnavailable(!isPartial);
        }
      })
      .catch((err) => {
        console.error("Failed to load Slack channels:", err);
        if (!cancelled) {
          setChannels([]);
          setUnavailable(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [connectionId]);

  return { channels, loading, unavailable, partial };
}

// ─── Display helpers ───

const CRON_DAY_NAMES: Record<string, string> = {
  "0": "Sun",
  "1": "Mon",
  "2": "Tue",
  "3": "Wed",
  "4": "Thu",
  "5": "Fri",
  "6": "Sat",
  "7": "Sun",
};

/**
 * Renders a 5-field cron as a short human schedule, e.g. "Weekdays 09:00 · America/Chicago",
 * "Every hour · UTC" or "Monthly (2nd Tuesday) 09:00 · Asia/Saigon". Falls back to the raw cron for
 * anything the form does not write, so a hand-typed cron still reads as what it is.
 */
export function formatCronSchedule(cron: string, tz: string): string {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return `${cron} · ${tz}`;
  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Interval presets carry no clock time.
  if (minute === "0" && dayOfMonth === "*" && month === "*" && dayOfWeek === "*") {
    if (hour === "*") return `Every hour · ${tz}`;
    const every = /^\*\/(\d{1,2})$/.exec(hour);
    if (every) return `Every ${every[1]} hours · ${tz}`;
  }

  const monthly = parseMonthlyCron(cron);
  if (monthly) {
    const time = `${String(monthly.hour).padStart(2, "0")}:00`;
    const when =
      monthly.mode === "lastDay"
        ? "last day"
        : monthly.mode === "dayOfWeek"
          ? `${ordinal(monthly.nth)} ${weekdayName(monthly.weekday)}`
          : `day ${monthly.day}`;
    return `Monthly (${when}) ${time} · ${tz}`;
  }

  if (!/^\d{1,2}$/.test(minute) || dayOfMonth !== "*" || month !== "*") {
    return `${cron} · ${tz}`;
  }
  // One or more whole hours: "09:00" or "09:00 & 17:00".
  if (!/^\d{1,2}(,\d{1,2})*$/.test(hour)) return `${cron} · ${tz}`;
  const time = hour
    .split(",")
    .map((h) => `${h.padStart(2, "0")}:${minute.padStart(2, "0")}`)
    .join(" & ");

  let days: string;
  if (dayOfWeek === "*") {
    days = "Every day";
  } else if (dayOfWeek === "1-5") {
    days = "Weekdays";
  } else if (/^[0-7](,[0-7])*$/.test(dayOfWeek)) {
    days = dayOfWeek
      .split(",")
      .map((d) => CRON_DAY_NAMES[d] ?? d)
      .join(", ");
  } else {
    return `${cron} · ${tz}`;
  }

  return `${days} ${time} · ${tz}`;
}
